<script setup lang="ts">
import BaseTextBlock from '~/components/BaseTextBlock.vue';
import CountryCodeDropdown from '~/components/CountryCodeDropdown.vue';
import BaseUpload from '~/components/BaseUpload.vue';
import { onMounted } from 'vue';
import { useRouter } from 'vue-router';
import BaseSelect from '~/components/BaseSelect.vue';
import { LANG } from '~/config/lang';
import BasePopup from '~/components/BasePopup.vue';
import { useApply } from '~/store/useApply';
import { useApplyError } from '~/store/useApplyError';
import { useApp } from '~/store/useApp';
import { IGetVacancy } from '~/types/api';

const config = useRuntimeConfig();
const applyStore = useApply();
const applyErrorStore = useApplyError();
const appStore = useApp();
const router = useRouter();

async function formSubmit(e: any) {
  let recaptchaToken = null;
  for (let elem of e.srcElement) {
    if (elem.id === 'g-recaptcha-response') {
      recaptchaToken = elem.value;
    }
  }
  const res = await applyStore.submitForm(
    recaptchaToken,
    (router?.currentRoute?.value?.params?.shortlinkId ?? '').toString()
  );

  if (res && res?.routeTo && res.routeTo !== '') {
    router.push(res?.routeTo);
  }
}

onMounted(() => {
  // @ts-ignore
  declare var grecaptcha: any;
  const interval = setInterval(() => {
    if (
      typeof grecaptcha !== 'undefined' &&
      typeof grecaptcha.render !== 'undefined'
    ) {
      grecaptcha.render('recapchaWidget', {
        sitekey: config.public.recapchaSiteKey,
      });
      clearInterval(interval);
    }
  }, 500);
});

const shortlinkId = (
  router?.currentRoute?.value?.params?.shortlinkId ?? ''
).toString();
const shortlinkBaseUrl = config.public.baseUrl;
const { data } = await useAsyncData('apply', () =>
  $fetch<IGetVacancy>(config.public.apiTransportRoute as string, {
    method: 'GET',
    query: {
      ShortlinkId: shortlinkId,
      ShortlinkBaseUrl: shortlinkBaseUrl,
    },
  })
);

appStore.fetchData(data.value?.data);

useHead({
  title: appStore.apiData?.JobInfo?.JobTitle,
  htmlAttrs: {
    prefix: 'og: http://ogp.me/ns#',
    xmlns: 'http://www.w3.org/1999/xhtml',
    'xmlns:og': 'http://ogp.me/ns#',
    'xmlns:fb': 'http://www.facebook.com/2008/fbml',
  },
  script: [
    {
      src: 'https://www.google.com/recaptcha/api.js?render=explicit',
      async: true,
      defer: true,
    },
  ],
  meta: [
    {
      property: 'og:url',
      content: `${config.public.baseUrl}/apply/${router.currentRoute.value.params.shortlinkId}`,
    },
    {
      property: 'og:image',
      name: 'twitter:image',
      // @ts-ignore
      itemprop: 'image primaryImageOfPage',
      content: appStore.apiData?.JobsiteSettings?.BannerImageUrl ?? '',
    },
    {
      property: 'og:title',
      name: 'twitter:title',
      // @ts-ignore
      itemprop: 'name',
      content: appStore.apiData?.JobInfo?.JobTitle,
    },
    {
      property: 'og:description',
      name: 'twitter:description',
      // @ts-ignore
      itemprop: 'description',
      content: appStore.apiData?.JobInfo.Description,
    },
    {
      property: 'og:site_name',
      content: 'MiniSite',
    },
    {
      property: 'og:type',
      content: 'website',
    },
    {
      name: 'twitter:card',
      content: appStore.apiData?.JobInfo.Description,
    },
    {
      property: 'og:image:type',
      content: 'image/jpeg',
    },
    {
      property: 'og:image:width',
      content: '150',
    },
    {
      property: 'og:image:height',
      content: '150',
    },
    {
      property: 'twitter:image',
      content: appStore.apiData?.JobsiteSettings?.BannerImageUrl ?? '',
    },
    {
      property: 'twitter:title',
      content: appStore.apiData?.JobInfo?.JobTitle,
    },
    {
      property: 'twitter:description',
      content: appStore.apiData?.JobInfo.Description,
    },
  ],
});

const factory = applyStore.changeStateHandleFactory;
const changeFirstNameHandle = factory<string>('firstName');
const changeLastNameHandle = factory<string>('lastName');
const changeMailHandle = factory<string>('mail');
const changeAreaCodeHandle = factory<string>('areaCode');
const changeAreaCodeHandle2 = factory<string>('areaCode2');
const changeMobileHandle = factory<string>('mobile');
const changeCVHandle = factory<any>('cv');
const changeAdditionalFilesHandle = factory<any>('additionalFiles');
const changeLinkedInHandle = factory<string>('linkedIn');
const changeNotesHandle = (notes: any) => {
  applyStore.notes = notes.target.value;
  applyErrorStore.notes = false;
};
const changePrivacyPolicyAcceptanceHandle = (event: any) => {
  applyStore.isUserAgreedToPrivacyPolicy = event.target.checked;
  applyErrorStore.isUserAgreedToPrivacyPolicy = false;
};
</script>
<template>
  <BaseLayout>
    <div class="flex flex-col items-center">
      <form action="" @submit.prevent="formSubmit">
        <div class="relative">
          <SocialShare
            :title="appStore?.apiData?.JobInfo?.JobTitle ?? ''"
            :description="appStore?.apiData?.JobInfo.Description ?? ''"
          />
        </div>

        <BaseTextBlock
          :title="LANG.personalInformation[appStore.lang]"
          styles="mt-[50px] lg:mt-[73px] lg:ml-[13px] pt-[15px] lg:pt-[27px]"
          :tabindex="10"
        >
          <div class="flex flex-col gap-[22px] relative" :dir="appStore.dir">
            <BaseInput
              :placeholder="LANG.firstName[appStore.lang]"
              :value="applyStore.firstName"
              @change="changeFirstNameHandle"
              :validate="applyErrorStore.firstName"
              no-logo
            />
            <BaseInput
              :placeholder="LANG.lastName[appStore.lang]"
              :value="applyStore.lastName"
              @change="changeLastNameHandle"
              :validate="applyErrorStore.lastName"
              no-logo
            />
            <BaseInput
              :placeholder="LANG.mail[appStore.lang]"
              :email="true"
              :value="applyStore.mail"
              @change="changeMailHandle"
              :validate="applyErrorStore.mail"
              no-logo
            />

            <div
              :class="[
                'flex bg-e flex-col lg:flex-row gap-[22px] ',
                appStore.lang === 'he' ? 'justify-end items-end' : '',
              ]"
              dir="ltr"
            >
              <div
                :class="[
                  'flex flex-row items-start sm:items-center w-full lg:w-[330px] ',
                  appStore.lang === 'he'
                    ? 'justify-end gap-4'
                    : 'justify-between',
                ]"
              >
                <p
                  class="text-dark text-[17px] mb-3 sm:mb-0"
                  :aria-label="LANG.countyAreaCode[appStore.lang]"
                >
                  {{ LANG.countyAreaCode[appStore.lang] }}
                </p>
                <CountryCodeDropdown
                  :dir="appStore.lang !== 'he' ? 'ltr' : 'rtl'"
                  :rtl="appStore.lang === 'he'"
                  @change="changeAreaCodeHandle"
                />
              </div>

              <div
                class="flex flex-row justify-between items-center sm:items-center w-[250px] sm:w-[292px] lg:w-[340px]"
              >
                <BaseInput
                  :value="applyStore.areaCode2"
                  :style="'w-[70px] sm:w-[80px] lg:w-[106px]'"
                  :placeholder="LANG.areaCode[appStore.lang]"
                  :validate="applyErrorStore.areaCode2"
                  @change="changeAreaCodeHandle2"
                  custom
                  number
                  no-logo
                  aria-label="Area code"
                />
                <p class="text-dark" aria-hidden="true">-</p>
                <BaseInput
                  :value="applyStore.mobile"
                  :style="'w-[160px] sm:w-[186px] lg:w-[208px]'"
                  @change="changeMobileHandle"
                  :placeholder="LANG.mobile[appStore.lang]"
                  :validate="applyErrorStore.mobile"
                  custom
                  number
                  no-logo
                  aria-label="mobile"
                />
              </div>
            </div>

            <BaseUpload
              :name="'cvFile'"
              :title="LANG.uploadCV[appStore.lang]"
              @change="changeCVHandle"
              :validate="applyErrorStore.cv"
              :is-multiple="false"
            />

            <BaseUpload
              v-if="config.public?.additionalFilesEnabled"
              :name="'additionalFiles'"
              :title="LANG.additionalFiles[appStore.lang]"
              @change="changeAdditionalFilesHandle"
              :validate="applyErrorStore.additionalFiles"
              :is-multiple="true"
              :max-files="(config.public?.maxAdditionalFiles as number) ?? 5"
              :max-file-size="(config.public?.maxFileSize as number) ?? 10"
              :max-files-size="(config.public?.maxFilesSize as number) ?? 20"
            />

            <BaseInput
              :placeholder="LANG.linkedIn[appStore.lang]"
              :value="applyStore.linkedIn"
              @change="changeLinkedInHandle"
              :validate="applyErrorStore.linkedIn"
              no-logo
            />

            <div
              v-for="(
                { AnswerType, Question, AnswerListValues, IsMandatory }, index
              ) in appStore.apiData?.JobsiteQuestions"
            >
              <BaseInput
                v-if="AnswerType === 'String'"
                :placeholder="IsMandatory ? `${Question} *` : Question"
                :index="index"
                :validate="applyErrorStore.questions[index]"
                @change="applyStore.questionHandle"
              />

              <BaseSelect
                v-if="AnswerType === null"
                :title="IsMandatory ? `${Question} *` : Question"
                :options="AnswerListValues ?? []"
                :index="index"
                :validate="applyErrorStore.questions[index]"
                @change="applyStore.questionHandle"
              />

              <YesNoSwitch
                v-if="AnswerType === 'Bool'"
                :title="IsMandatory ? `${Question} *` : Question"
                :index="index"
                :validate="applyErrorStore.questions[index]"
                @change="applyStore.questionHandle"
              />
            </div>

            <div
              v-for="({ Text, Link, Required, ClientIP }, index) in appStore
                .apiData?.JobInfo?.Gdpr ?? []"
            >
              <GDPR
                :index="index"
                :text="Text"
                :link="Link"
                :required="Required"
                :client-ip="ClientIP"
                :value="applyStore.gdpr[index] ?? false"
                :validate="applyErrorStore.gdpr[index]"
                @change="applyStore.gdprHandle"
              />
            </div>

            <BaseTextarea
              v-model="applyStore.notes"
              @change="changeNotesHandle"
              :placeholder="LANG.notes[appStore.lang]"
            />

            <!--            <BaseCheckbox-->
            <!--                v-if="appStore.apiData?.JobsiteQuestions?.length && appStore.lang !== 'he'"-->
            <!--                :title="LANG.iConfirm[appStore.lang]"-->
            <!--                :value="applyStore.confirm"-->
            <!--                @change="changeConfirmHandle"-->
            <!--                :validate="applyErrorStore.confirm"-->
            <!--            />-->

            <div
              id="recapchaWidget"
              class="g-recaptcha"
              :data-sitekey="config.recapchaSiteKey"
            />

            <!-- Privacy Policy Acceptance Checkbox -->
            <div
              v-if="appStore.apiData?.PrivacyPolicyUrl"
              class="lg:ml-[12px] mt-[16px] w-full lg:w-[800px]"
            >
              <div :class="['flex gap-2 items-start']">
                <input
                  type="checkbox"
                  name="isUserAgreedToPrivacyPolicy"
                  id="isUserAgreedToPrivacyPolicy"
                  v-model="applyStore.isUserAgreedToPrivacyPolicy"
                  class="accent-ui mt-1"
                  @change="changePrivacyPolicyAcceptanceHandle"
                />
                <label
                  :class="[
                    'text-[14px] text-dark cursor-pointer',
                    applyErrorStore.isUserAgreedToPrivacyPolicy
                      ? 'text-red-500'
                      : '',
                  ]"
                  for="isUserAgreedToPrivacyPolicy"
                >
                  {{ LANG.privacyPolicyAcceptance[appStore.lang] }}
                  <a
                    :href="appStore.apiData.PrivacyPolicyUrl"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="text-blue-600 hover:text-blue-800 underline transition-colors ml-1"
                  >
                    {{ LANG.privacyPolicy[appStore.lang] }}
                  </a>
                </label>
              </div>
            </div>
          </div>
        </BaseTextBlock>

        <BaseButton
          :text="LANG.submitApplication[appStore.lang]"
          class="lg:ml-[12px] mt-[16px] w-full lg:w-[800px]"
          color="#2C9EB3"
          :loading="applyStore.isLoading"
        />
      </form>
    </div>

    <ClientOnly>
      <BasePopup />
    </ClientOnly>
  </BaseLayout>
</template>
<style>
input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

input[type='number'] {
  -moz-appearance: textfield;
  appearance: textfield;
}
</style>
